import { useZero } from "@rocicorp/zero/react";
import { useCallback, useEffect, useRef } from "react";
import { sharedMutators } from "../../shared/mutators";
import { isConversationalEntry, recapThroughEntryId } from "../../shared/recap";

const RECAP_STALE_MS = 3 * 60 * 1000;

type TriggerEntry = {
  id: string;
  entryType: string;
  createdAt: number;
  recap?: { throughEntryId: string } | null;
};

export function useRecapTrigger({
  chatId,
  entries,
  responding,
  active,
}: {
  chatId: string | null;
  entries: readonly TriggerEntry[];
  responding: boolean;
  active: boolean;
}) {
  const z = useZero();
  const requestedForRef = useRef<string | null>(null);

  const target = recapThroughEntryId(entries);
  const latestCreatedAt = entries
    .filter(isConversationalEntry)
    .at(-1)?.createdAt;

  const maybeGenerate = useCallback(() => {
    if (!active || responding || !chatId) return;
    if (!target || latestCreatedAt == null) return;
    if (Date.now() - latestCreatedAt < RECAP_STALE_MS) return;
    if (requestedForRef.current === target) return;
    requestedForRef.current = target;
    z.mutate(sharedMutators.generateChatRecap({ chatId }));
  }, [active, responding, chatId, target, latestCreatedAt, z]);

  useEffect(() => {
    maybeGenerate();
    const onVisible = () => {
      if (!document.hidden) maybeGenerate();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", maybeGenerate);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", maybeGenerate);
    };
  }, [maybeGenerate]);
}
